import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiFetch } from '@/lib/api';
import { subscribeToLeadUpdates, broadcastLeads } from '@/utils/broadcastChannel';

/**
 * Leads vom Server (/api/leads). Der Server ist die Quelle der Wahrheit —
 * es gibt KEINEN localStorage-Fallback mehr. Schlaegt das Laden fehl, meldet
 * der Hook einen ehrlichen Fehlerzustand (loadError) und eine leere Liste,
 * damit niemand veraltete Browserdaten fuer aktuell haelt.
 *
 * WICHTIG — zwei getrennte Fehlerzustaende:
 *   loadError  : nur das Laden ist fehlgeschlagen. Es gibt keine belastbaren
 *                Daten, also darf die Liste/Detailseite nicht angezeigt werden.
 *   writeError : eine Aenderung (anlegen/aendern/loeschen) ist fehlgeschlagen.
 *                Die geladenen Daten sind weiterhin gueltig und muessen sichtbar
 *                bleiben — der Fehler wird nur als Hinweis eingeblendet.
 * Frueher teilten sich beide einen State, wodurch ein fehlgeschlagener
 * Statuswechsel die komplette (intakte) Liste ausgeblendet hat.
 */

export const LEAD_STATUSES = [
  { value: 'neu', label: 'Neu', color: 'blue' },
  { value: 'qualifiziert', label: 'Qualifiziert', color: 'purple' },
  { value: 'angebot_erstellt', label: 'Angebot erstellt', color: 'indigo' },
  { value: 'angebot_gesendet', label: 'Angebot gesendet', color: 'cyan' },
  { value: 'angebot_geoeffnet', label: 'Angebot geoeffnet', color: 'amber' },
  { value: 'in_verhandlung', label: 'In Verhandlung', color: 'orange' },
  { value: 'angenommen', label: 'Angenommen', color: 'green' },
  { value: 'vertrag', label: 'Vertrag', color: 'emerald' },
  { value: 'verloren', label: 'Verloren', color: 'red' },
];

export const PRIORITY_OPTIONS = [
  { value: 'niedrig', label: 'Niedrig' },
  { value: 'normal', label: 'Normal' },
  { value: 'hoch', label: 'Hoch' },
  { value: 'dringend', label: 'Dringend' },
];

export const SOURCE_OPTIONS = [
  'Kaltakquise', 'Empfehlung', 'Website', 'Messe', 'Bestandskunde', 'Sonstiges'
];

/**
 * Felder, die PUT /api/leads/:id serverseitig akzeptiert.
 * Alles andere (Firmen-/Kontaktdaten, source) wird vom Backend verworfen —
 * deshalb schicken wir es gar nicht erst mit und tun auch lokal nicht so,
 * als waere es gespeichert worden.
 */
export const LEAD_UPDATE_FIELDS = [
  'status', 'priority', 'notes', 'next_action', 'next_action_date',
  'assigned_to', 'lost_reason',
];

const DATE_FIELDS = ['next_action_date'];

/** Leere Strings zu null — eine DATE-Spalte akzeptiert '' nicht. */
const normalizeValue = (key, value) => {
  if (DATE_FIELDS.includes(key)) return value ? value : null;
  return value;
};

/** Fehlermeldung aus einer fehlgeschlagenen Antwort ziehen. */
const readError = async (resp, fallback) => {
  try {
    const data = await resp.json();
    return data?.error || fallback;
  } catch {
    return fallback;
  }
};

export const useLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [writeError, setWriteError] = useState('');

  const clearWriteError = useCallback(() => setWriteError(''), []);

  /**
   * Schreibfehler festhalten UND als Exception zurueckgeben, damit die
   * aufrufende Seite ihn zusaetzlich lokal (z.B. im Dialog) behandeln kann.
   */
  const writeFailure = useCallback((message, fallback) => {
    const msg = message || fallback;
    setWriteError(msg);
    return new Error(msg);
  }, []);

  // Synchroner Spiegel des States: wird fuer optimistische Updates und
  // das Zuruecksetzen bei Fehlern gebraucht (setState-Updater laufen
  // erst beim Rendern, nicht sofort beim Aufruf).
  const leadsRef = useRef([]);
  const commit = useCallback((next) => {
    leadsRef.current = next;
    setLeads(next);
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const resp = await apiFetch('/api/leads');
      if (!resp.ok) throw new Error(await readError(resp, 'Leads konnten nicht geladen werden'));
      const data = await resp.json();
      leadsRef.current = Array.isArray(data) ? data : [];
      setLeads(leadsRef.current);
      // Nur den LADEfehler zuruecksetzen: ein offener Schreibfehler bleibt
      // stehen, bis der Nutzer ihn gesehen und weggeklickt hat.
      setLoadError('');
    } catch (err) {
      leadsRef.current = [];
      setLeads([]);
      setLoadError(err.message || 'Leads konnten nicht geladen werden');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const reload = useCallback(() => load(), [load]);

  // Initiales Laden
  useEffect(() => { load(); }, [load]);

  // Cross-Tab-Sync: NICHT die fremde Liste uebernehmen (die koennte veraltet
  // sein), sondern frisch vom Server nachladen.
  useEffect(() => {
    const unsub = subscribeToLeadUpdates(() => { load({ silent: true }); });
    return unsub;
  }, [load]);

  /** Andere Tabs anstossen, damit sie neu vom Server laden. */
  const notifyOtherTabs = useCallback(() => {
    try {
      broadcastLeads(leadsRef.current);
    } catch {
      // BroadcastChannel ist optional — ohne Cross-Tab-Sync geht es auch.
    }
  }, []);

  const addLead = useCallback(async (leadData) => {
    const payload = {
      company: leadData.company || {},
      contact: leadData.contact || {},
      status: leadData.status || 'neu',
      priority: leadData.priority || 'normal',
      source: leadData.source || null,
      notes: leadData.notes || null,
      next_action: leadData.next_action || null,
      next_action_date: leadData.next_action_date || null,
    };

    let created;
    try {
      const resp = await apiFetch('/api/leads', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(await readError(resp, 'Lead konnte nicht angelegt werden'));
      created = await resp.json();
      if (!created?.id) throw new Error('Server hat keinen Lead zurueckgegeben');
    } catch (err) {
      throw writeFailure(err.message, 'Lead konnte nicht angelegt werden');
    }

    // Falls zwischenzeitlich neu geladen wurde, den Lead nicht doppelt fuehren.
    commit([created, ...leadsRef.current.filter(l => String(l.id) !== String(created.id))]);
    setWriteError('');
    notifyOtherTabs();
    return created;
  }, [commit, notifyOtherTabs, writeFailure]);

  const updateLead = useCallback(async (id, changes) => {
    const payload = {};
    for (const key of LEAD_UPDATE_FIELDS) {
      if (changes && Object.prototype.hasOwnProperty.call(changes, key)) {
        payload[key] = normalizeValue(key, changes[key]);
      }
    }

    if (Object.keys(payload).length === 0) {
      throw writeFailure('', 'Keine serverseitig speicherbaren Felder enthalten');
    }

    const snapshot = leadsRef.current;
    const target = snapshot.find(l => String(l.id) === String(id));
    if (!target) {
      throw writeFailure('', 'Lead nicht gefunden');
    }

    // Optimistisch anwenden …
    commit(snapshot.map(l =>
      String(l.id) === String(id)
        ? { ...l, ...payload, updated_at: new Date().toISOString() }
        : l
    ));

    let saved;
    try {
      const resp = await apiFetch(`/api/leads/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(await readError(resp, 'Aenderung konnte nicht gespeichert werden'));
      saved = await resp.json();
      if (!saved?.id) throw new Error('Lead auf dem Server nicht gefunden');
    } catch (err) {
      // … und bei Fehler wieder zuruecknehmen.
      commit(snapshot);
      throw writeFailure(err.message, 'Aenderung konnte nicht gespeichert werden');
    }

    // Server-Zeile uebernehmen. Sie enthaelt kein company/contact —
    // die bereits geladenen Objekte bleiben erhalten.
    commit(leadsRef.current.map(l =>
      String(l.id) === String(id)
        ? { ...l, ...saved, company: l.company, contact: l.contact }
        : l
    ));
    setWriteError('');
    notifyOtherTabs();
    return saved;
  }, [commit, notifyOtherTabs, writeFailure]);

  const updateLeadStatus = useCallback((id, status) => {
    return updateLead(id, { status });
  }, [updateLead]);

  const deleteLead = useCallback(async (id) => {
    const snapshot = leadsRef.current;
    commit(snapshot.filter(l => String(l.id) !== String(id)));

    try {
      const resp = await apiFetch(`/api/leads/${id}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error(await readError(resp, 'Lead konnte nicht geloescht werden'));
    } catch (err) {
      commit(snapshot);
      throw writeFailure(err.message, 'Lead konnte nicht geloescht werden');
    }

    setWriteError('');
    notifyOtherTabs();
  }, [commit, notifyOtherTabs, writeFailure]);

  // Server-IDs sind Zahlen, useParams liefert Strings — immer als String vergleichen.
  const getLeadById = useCallback((id) => {
    if (id === undefined || id === null) return null;
    return leads.find(l => String(l.id) === String(id)) || null;
  }, [leads]);

  // Statistiken
  const stats = useMemo(() => ({
    total: leads.length,
    neu: leads.filter(l => l.status === 'neu').length,
    offen: leads.filter(l => !['vertrag', 'verloren'].includes(l.status)).length,
    angenommen: leads.filter(l => l.status === 'angenommen' || l.status === 'vertrag').length,
    verloren: leads.filter(l => l.status === 'verloren').length,
  }), [leads]);

  return {
    leads,
    loading,
    loadError,
    writeError,
    clearWriteError,
    // Alias fuer aeltere Aufrufer: `error` meint AUSSCHLIESSLICH den Ladefehler.
    error: loadError,
    stats,
    addLead,
    updateLead,
    updateLeadStatus,
    deleteLead,
    getLeadById,
    reload,
  };
};

export default useLeads;
